import { useState } from "react"
import styled from "styled-components"

import { spacing } from "@minuthq/meatball-ui-react"
import { Text } from "@minuthq/meatball-ui-react"

import {
  SettingContainer,
  TSettingContainerOnSaveReturnType,
} from "src/components/Settings/Setting/SettingContainer"
import { usePostIntegrationMessagingPlatformEdited } from "src/data/analytics/queries/integrationAnalyticsQueries"
import { usePatchCommunicationChannel } from "src/data/integrations/queries/integrationQueries"
import {
  OwnerType,
  TIntegration,
  TIntegrationCommunicationChannels,
} from "src/data/integrations/types/integrationTypes"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { useTranslate } from "src/i18n/useTranslate"
import { MDivider } from "src/ui/Divider/Divider"
import { MInfo } from "src/ui/Info/MInfo"
import { MBadge } from "src/ui/MBadge/MBadge"
import { MCheckbox } from "src/ui/MCheckbox/MCheckbox"

// This is only guest specific for now. Any new integrations that need messaging channels
// will require that the proper integrations settings structure is added to the backend
// and this component refactored to support dynamic messaging channels
export function MessagingChannel({
  integration,
}: {
  integration: TIntegration
}) {
  if (!integration.settings) {
    return null
  }

  return (
    <div>
      <GuestyMessagingOptions integration={integration} />
    </div>
  )
}

function GuestyMessagingOptions({
  integration,
}: {
  integration: TIntegration
}) {
  const { t, langKeys } = useTranslate()
  const { orgId } = useOrganization()

  const patchCommsChannel = usePatchCommunicationChannel({ orgId })
  const postIntegrationMessagingPlatformEdited =
    usePostIntegrationMessagingPlatformEdited()

  const communicationChannels =
    integration.settings?.guest_communication_channels

  const [minutChannel, setMinutChannel] = useState<
    TIntegrationCommunicationChannels | undefined
  >(communicationChannels?.find((c) => c === "minut"))
  const [partnerChannel, setPartnerChannel] = useState<
    TIntegrationCommunicationChannels | undefined
  >(communicationChannels?.find((c) => c === "partner"))

  async function handleUpdateMessagingChannel(): TSettingContainerOnSaveReturnType {
    const filteredChannels = [minutChannel, partnerChannel].filter((c) => !!c)

    try {
      await patchCommsChannel.mutateAsync({
        path: {
          integration_identifier: integration.integration_identifier,
        },
        body: {
          guest_communication_channels: filteredChannels,
        },
        query: {
          owner_id: orgId,
          owner_type: OwnerType.ORGANIZATION,
        },
      })

      // #region tracking
      const removed: TIntegrationCommunicationChannels[] =
        communicationChannels?.filter((c) => !filteredChannels.includes(c)) ||
        []
      const added = filteredChannels.filter(
        (c) => !communicationChannels?.includes(c)
      )

      if (removed?.length > 0) {
        postIntegrationMessagingPlatformEdited.mutate({
          integration_name: integration.integration_identifier,
          type: removed.includes("minut")
            ? "sms_from_minut"
            : "integration_inbox",
          action: "turn_off",
        })
      }

      if (added?.length > 0) {
        postIntegrationMessagingPlatformEdited.mutate({
          integration_name: integration.integration_identifier,
          type: added.includes("minut")
            ? "sms_from_minut"
            : "integration_inbox",
          action: "turn_on",
        })
      }

      // #endregion Tracking

      return {
        isSuccess: true,
      }
    } catch (error) {
      return {
        isSuccess: true,
      }
    }
  }

  return (
    <MessagingBox>
      <SettingContainer
        title={
          <Text variant="heading3">
            {t(langKeys.integrations_messaging_channel_section_label)}
          </Text>
        }
        description={t(
          langKeys.integrations_messaging_channel_guesty_description
        )}
        hasError={!minutChannel && !partnerChannel}
        onSave={handleUpdateMessagingChannel}
        displayValue={
          <DisplayBox>
            <ChannelDisplayBox>
              <Text>{t(langKeys.integrations_messaging_channel_minut)}</Text>
              <div>
                <MBadge color={!!minutChannel ? "info" : "neutral"}>
                  {!!minutChannel ? t(langKeys.on) : t(langKeys.off)}
                </MBadge>
              </div>
            </ChannelDisplayBox>

            <ChannelDisplayBox>
              <Text>
                {t(langKeys.integrations_messaging_channel_guesty_inbox)}
              </Text>
              <div>
                <MBadge color={!!partnerChannel ? "info" : "neutral"}>
                  {!!partnerChannel ? t(langKeys.on) : t(langKeys.off)}
                </MBadge>
              </div>
            </ChannelDisplayBox>
          </DisplayBox>
        }
        InputComponent={() => (
          <div>
            <SettingsBox>
              <MCheckbox
                label={
                  <Text>
                    {t(langKeys.integrations_messaging_channel_minut)}
                  </Text>
                }
                checked={!!minutChannel}
                onCheck={(v) => setMinutChannel(v ? "minut" : undefined)}
              />
              <MCheckbox
                label={
                  <PartnerBox>
                    <Text>
                      {t(langKeys.integrations_messaging_channel_guesty_inbox)}
                    </Text>
                    <MInfo
                      content={t(
                        langKeys.integrations_messaging_channel_guesty_tooltip
                      )}
                    />
                  </PartnerBox>
                }
                checked={!!partnerChannel}
                onCheck={(v) => setPartnerChannel(v ? "partner" : undefined)}
              />
              <MDivider />
            </SettingsBox>
          </div>
        )}
      />

      <MDivider />
    </MessagingBox>
  )
}

const MessagingBox = styled.div`
  display: grid;
  gap: ${spacing.M};
  margin-bottom: ${spacing.XL2};
`

const SettingsBox = styled.div`
  display: flex;
  gap: ${spacing.XL};
  padding: ${spacing.S};
`

const PartnerBox = styled.div`
  display: flex;
  gap: ${spacing.XS2};
  align-items: center;
  flex-wrap: nowrap;
`

const DisplayBox = styled.div`
  display: flex;
  gap: ${spacing.XL};
  flex-wrap: wrap;
`

const ChannelDisplayBox = styled.div`
  display: flex;
  gap: ${spacing.S};
`
